import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Users, Star, Calendar, Clock, ArrowRight, Anchor, Waves, Sun, Palmtree, Fish, Compass } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
const API = `${BACKEND_URL}/api`;

// Destination data
const destinationData = {
  'caribbean': {
    name: 'Caribbean',
    subtitle: 'Tropical Paradise',
    description: 'Discover the crystal-clear waters and white sand beaches of the Caribbean, where each island tells a story of natural beauty and tropical luxury. A journey to this paradise transports you to a world where time seems suspended.',
    heroImage: 'https://images.unsplash.com/photo-1719664026730-85137c88c7b9',
    season: 'December - April',
    temperature: '24-28°C',
    conditions: 'Regular trade winds, calm seas',
    highlights: [
      'Crystal turquoise waters',
      'Pristine white sand beaches',
      'Legendary sunsets',
      'Refined Creole cuisine',
      'Authentic island cultures',
      'Exceptional water sports'
    ],
    filterLocation: 'Caribbean',
    itineraries: [
      {
        id: 1,
        title: 'Saint-Barthélemy & Anguilla',
        duration: '7 days',
        difficulty: 'Easy',
        description: 'A dream journey between French sophistication and authentic Caribbean charm.',
        dreamDescription: 'Imagine waking up each morning on impossibly blue waters, where the discreet luxury of Saint-Barth meets the preserved tranquility of Anguilla. Your days flow between private beaches, gourmet lunches with your feet in the sand, and evenings under an unparalleled starry sky.',
        highlights: [
          'Gustavia - Luxury shopping and French art of living',
          'Gouverneur Beach - White sand and absolute intimacy', 
          'Anguilla - Endless beaches and restaurants on stilts',
          'Prickly Pear Cays - Desert islets for private picnics'
        ],
        route: 'Saint-Barthélemy → Gustavia → Anguilla → Prickly Pear → Sandy Island → Return Saint-Barth'
      },
      {
        id: 2,
        title: 'British Virgin Islands',
        duration: '10 days',
        difficulty: 'Moderate',
        description: 'The secret archipelago of navigators, where each anchorage reveals a new wonder.',
        dreamDescription: 'Navigate in the most beautiful nautical playground in the world, where islands follow each other like pearls on a precious necklace. Each stop offers you a new paradise: secret coves, legendary floating bars, and sunsets that redefine beauty.',
        highlights: [
          'Virgin Gorda - The Baths and unique rock formations',
          'Jost Van Dyke - Foxy\'s Bar and authentic Caribbean atmosphere',
          'Anegada - Coral reefs and giant lobsters',
          'Cooper Island - Rum Bar and exceptional diving'
        ],
        route: 'Tortola → Virgin Gorda → Jost Van Dyke → Sandy Cay → Anegada → Cooper Island → Norman Island'
      },
      {
        id: 3,
        title: 'Antigua & Barbuda Explorer',
        duration: '5 days',
        difficulty: 'Easy',
        description: 'Express discovery of the Lesser Antilles jewels.',
        dreamDescription: 'A perfect escape where maritime history meets tropical luxury. Antigua welcomes you with its 365 beaches - one for each day of the year - while Barbuda offers you one of the Caribbean\'s best-kept secrets.',
        highlights: [
          'English Harbour - Naval history and prestigious marina',
          'Half Moon Bay - One of the most beautiful beaches in the world',
          'Barbuda - Pink Sand Beach and frigate bird watching',
          'Deep Bay - Wreck accessible by snorkeling'
        ],
        route: 'English Harbour → Jolly Harbour → Barbuda → Two Foot Bay → Green Island → Return Antigua'
      }
    ]
  },
  'monaco': {
    name: 'Monaco',
    subtitle: 'Exceptional Principality',
    description: 'Discover Monaco, jewel of the Mediterranean where luxury reaches its quintessence. Between legendary glamour and refined tradition, this principality offers an incomparable yachting experience in the heart of the French Riviera.',
    heroImage: 'https://images.unsplash.com/photo-1617330666625-e75bee2e333b',
    season: 'May - September',
    temperature: '22-28°C',
    conditions: 'Calm seas, light winds',
    highlights: [
      'Prestigious Port Hercule',
      'Legendary Monte-Carlo Casino',
      'Formula 1 Grand Prix',
      'Michelin-starred gastronomy',
      'Haute couture shopping',
      'Exclusive nightlife'
    ],
    filterLocation: 'Monaco',
    itineraries: [
      {
        id: 1,
        title: 'Monaco Prestige & Riviera',
        duration: '5 days',
        difficulty: 'Easy',
        description: 'The essence of Mediterranean glamour between Monaco and the French Riviera.',
        dreamDescription: 'Navigate in waters where the world\'s most beautiful yachts meet. Monaco welcomes you in its mythical port, while Cannes and Antibes reveal their hidden treasures. Each stop is a journey into the Mediterranean art of living.',
        highlights: [
          'Port Hercule - World\'s most prestigious marina',
          'Monte-Carlo Casino - Legendary evenings',
          'Cannes - Festival and glamorous Croisette',
          'Antibes - Old town and Provençal market'
        ],
        route: 'Monaco → Cannes → Antibes → Nice → Cap Ferrat → Return Monaco'
      },
      {
        id: 2,
        title: 'Exclusive Riviera',
        duration: '7 days',
        difficulty: 'Moderate',
        description: 'In-depth discovery of the French Riviera\'s secret jewels.',
        dreamDescription: 'Explore the secret coves and perched villages that make the magic of the Riviera. From Monaco to Saint-Tropez, each anchorage reveals a unique facet of this legendary coast, between preserved nature and French sophistication.',
        highlights: [
          'Lérins Islands - Preserved nature facing Cannes',
          'Saint-Jean-Cap-Ferrat - Villa Ephrussi and gardens',
          'Villefranche-sur-Mer - Deep harbor and architecture',
          'Beaulieu-sur-Mer - Provençal sweetness of life'
        ],
        route: 'Monaco → Lérins Islands → Saint-Jean-Cap-Ferrat → Villefranche → Beaulieu → Return Monaco'
      }
    ]
  },
  'saint-tropez': {
    name: 'Saint-Tropez',
    subtitle: 'Mediterranean Art of Living',
    description: 'Saint-Tropez embodies the relaxed elegance of the French Riviera. This mythical destination combines Provençal tradition and contemporary sophistication in an exceptional Mediterranean setting.',
    heroImage: 'https://images.unsplash.com/photo-1669428521191-5aef429aa1e5',
    season: 'June - September',
    temperature: '24-30°C',
    conditions: 'Refreshing Mistral, azure sea',
    highlights: [
      'Iconic Old Port',
      'Legendary beaches (Pampelonne)',
      'Saint-Tropez art of living',
      'Provençal markets',
      'Renowned vineyards',
      'Refined nightlife'
    ],
    filterLocation: 'Saint-Tropez',
    itineraries: [
      {
        id: 1,
        title: 'Authentic Saint-Tropez',
        duration: '4 days',
        difficulty: 'Easy',
        description: 'Immersion in the Saint-Tropez art of living between tradition and modernity.',
        dreamDescription: 'Discover the magic of Saint-Tropez away from the crowds, when the first rays of sun caress the Old Port. Stroll through colorful markets, savor a local rosé facing the Maures mountains, and let yourself be carried away by the Provençal sweetness of life.',
        highlights: [
          'Old Port - Mythical terraces and fishermen',
          'Citadel - Exceptional panorama over the gulf',
          'Place des Lices - Provençal market and pétanque',
          'Pampelonne Beach - Saint-Tropez legend'
        ],
        route: 'Saint-Tropez → Ramatuelle → Gassin → Port Grimaud → Return Saint-Tropez'
      },
      {
        id: 2,
        title: 'Gulf of Saint-Tropez',
        duration: '6 days',
        difficulty: 'Moderate',
        description: 'Complete exploration of the gulf and its hidden treasures.',
        dreamDescription: 'Navigate in the crystal-clear waters of the Gulf of Saint-Tropez, where each cove reveals a secret. From the perched villages of the hinterland to secret beaches, discover an authentic and preserved French Riviera.',
        highlights: [
          'Sainte-Maxime - Family charm and traditions',
          'Port Grimaud - Provençal Venice',
          'Hyères Islands - Porquerolles and Port-Cros',
          'Bormes-les-Mimosas - Flowery village'
        ],
        route: 'Saint-Tropez → Sainte-Maxime → Hyères Islands → Bormes → Le Lavandou → Return Saint-Tropez'
      }
    ]
  },
  'greek-islands': {
    name: 'Greek Islands',
    subtitle: 'Eternal Splendor of the Cyclades',
    description: 'Navigate to the Greek islands, where ancient mythology meets Mediterranean beauty. Each Cycladic island reveals its unique character in a setting of white houses and crystal-clear waters.',
    heroImage: 'https://images.unsplash.com/photo-1664268406930-fa8a02c6e09f',
    season: 'April - October',
    temperature: '20-28°C',
    conditions: 'Meltemi winds, azure sea',
    highlights: [
      'Unique Cycladic architecture',
      'Oia sunsets',
      'Authentic Greek gastronomy',
      'Millenary ancient history',
      'Crystal-clear water beaches',
      'Legendary Greek hospitality'
    ],
    filterLocation: 'Greek Islands',
    itineraries: [
      {
        id: 1,
        title: 'Classic Cyclades',
        duration: '8 days',
        difficulty: 'Moderate',
        description: 'The pearls of the Cyclades: Mykonos, Santorini and their secret jewels.',
        dreamDescription: 'Sail to the mythical islands where the immaculate white of houses contrasts with the infinite blue of the Aegean Sea. Mykonos seduces you with its cosmopolitan energy, Santorini amazes you with its legendary sunsets, and each stop reveals the authentic soul of Greece.',
        highlights: [
          'Mykonos - Windmills and nightlife',
          'Santorini - Caldera and perched villages',
          'Paros - White marble and typical villages',
          'Naxos - Temple of Apollo and golden beaches'
        ],
        route: 'Athens → Mykonos → Paros → Naxos → Santorini → Ios → Return Athens'
      },
      {
        id: 2,
        title: 'Secret Cyclades',
        duration: '10 days',
        difficulty: 'Moderate',
        description: 'Discovery of preserved islands off the beaten path.',
        dreamDescription: 'Explore the hidden treasures of the Cyclades, where time seems suspended. Folegandros offers you its dramatic cliffs, Koufonisia its paradisiacal beaches, and Amorgos its preserved authenticity. A timeless journey in eternal Greece.',
        highlights: [
          'Folegandros - Spectacular cliffs and tranquility',
          'Koufonisia - Paradisiacal beaches and turquoise water',
          'Amorgos - Hozoviotissa Monastery',
          'Sifnos - Refined Greek gastronomy'
        ],
        route: 'Athens → Sifnos → Folegandros → Koufonisia → Amorgos → Astypalaia → Return Athens'
      }
    ]
  },
  'amalfi': {
    name: 'Amalfi Coast',
    subtitle: 'Eternal Romance of Italy',
    description: 'The Amalfi Coast unfolds its charms between sky and sea, where each perched village tells a love story with the Mediterranean. Positano, Amalfi and Capri reveal Italian beauty in all its splendor.',
    heroImage: 'https://images.unsplash.com/photo-1594453987501-8ae427f3243b',
    season: 'May - October',
    temperature: '22-28°C',
    conditions: 'Calm seas, sea breezes',
    highlights: [
      'Spectacular perched villages',
      'Refined Italian gastronomy',
      'Dramatic and colorful coast',
      'Millenary history',
      'Authentic local craftsmanship',
      'Romantic sunsets'
    ],
    filterLocation: 'Amalfi Coast',
    itineraries: [
      {
        id: 1,
        title: 'Pearls of Amalfi',
        duration: '6 days',
        difficulty: 'Easy',
        description: 'The jewels of the coast: Positano, Amalfi and magical Capri.',
        dreamDescription: 'Let yourself be seduced by the poetry of the Amalfi coast, where each village cascades towards the sea like a master painting. Positano enchants you with its colorful houses, Amalfi with its maritime history, and Capri with its legendary beauty.',
        highlights: [
          'Positano - Colorful cascading houses',
          'Amalfi - Cathedral and maritime history',
          'Capri - Blue Grotto and Augustus Gardens',
          'Ravello - Villa Rufolo and panoramas'
        ],
        route: 'Naples → Positano → Amalfi → Capri → Sorrento → Ravello → Return Naples'
      },
      {
        id: 2,
        title: 'Complete Bay of Naples',
        duration: '8 days',
        difficulty: 'Moderate',
        description: 'In-depth exploration of the Neapolitan bay and its treasures.',
        dreamDescription: 'Discover all the secrets of this mythical bay, from the ruins of Pompeii to the fragrant gardens of Ischia. Each stop reveals a facet of eternal Italy, between ancient history and contemporary dolce vita.',
        highlights: [
          'Ischia - Natural thermal baths and vineyards',
          'Procida - Authenticity and pastel colors',
          'Pompeii - History frozen in time',
          'Sorrento Coast - Lemons and panoramas'
        ],
        route: 'Naples → Ischia → Procida → Sorrento → Positano → Amalfi → Capri → Return Naples'
      }
    ]
  },
  'ibiza': {
    name: 'Ibiza',
    subtitle: 'Electrifying Energy of the Balearics',
    description: 'Ibiza reveals its multiple facets: white island with authentic villages, world capital of electronic music, and sanctuary of creative freedom. A destination where Mediterranean tradition and bohemian spirit blend.',
    heroImage: 'https://images.pexels.com/photos/3834352/pexels-photo-3834352.jpeg',
    season: 'June - September',
    temperature: '24-30°C',
    conditions: 'Tramontane winds, turquoise sea',
    highlights: [
      'Legendary nightlife',
      'Preserved wild coves',
      'Authentic white villages',
      'Sunsets at Café del Mar',
      'Colorful hippie markets',
      'Mediterranean gastronomy'
    ],
    filterLocation: 'Ibiza',
    itineraries: [
      {
        id: 1,
        title: 'Authentic Ibiza',
        duration: '5 days',
        difficulty: 'Easy',
        description: 'The true soul of Ibiza between tradition and modernity.',
        dreamDescription: 'Découvrez l\'Ibiza authentique, loin des clichés, où les villages blancs révèlent leur charme ancestral. Entre criques secrètes et marchés colorés, plongez dans l\'esprit libre et créatif qui fait l\'essence de l\'île blanche.',
        highlights: [
          'Dalt Vila - Vieille ville fortifiée UNESCO',
          'San José - Villages blancs traditionnels',
          'Cala Comte - Couchers de soleil légendaires',
          'Marchés de Las Dalias - Artisanat bohème'
        ],
        route: 'Ibiza → San Antonio → San José → Santa Eulalia → Es Canar → Retour Ibiza'
      },
      {
        id: 2,
        title: 'Baléares Complètes',
        duration: '7 jours',
        difficulty: 'Modérée',
        description: 'Tour complet des Baléares : Ibiza, Formentera et leurs secrets.',
        dreamDescription: 'Explorez l\'archipel des Baléares dans toute sa diversité. D\'Ibiza l\'électrisante à Formentera la sauvage, chaque île révèle son caractère unique dans un écrin méditerranéen d\'exception.',
        highlights: [
          'Formentera - Plages paradisiaques et eaux cristallines',
          'Es Vedra - Îlot mystique et énergies',
          'Cala Bassa - Lagon turquoise et pins',
          'Platja de Ses Illetes - Plus belle plage d\'Europe'
        ],
        route: 'Ibiza → Formentera → Es Vedra → Cala Bassa → San Antonio → Santa Eulalia → Retour Ibiza'
      }
    ]
  }
};

// Header Component for destination pages
const Header = ({ isTransparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const shouldBeTransparent = isTransparent && !isScrolled;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${shouldBeTransparent ? 'bg-transparent' : 'bg-white/95 backdrop-blur-lg shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <a href="/" className={`text-2xl font-light tracking-wide transition-colors ${shouldBeTransparent ? 'text-white' : 'text-charcoal-900'}`}>
              PRIVIA<span className="font-thin">CHARTER</span>
            </a>
          </div>
          
          <nav className="hidden lg:flex space-x-12">
            <a href="/escapes" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>ESCAPES</a>
            <a href="/collection" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>COLLECTION</a>
            <a href="/experience" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>EXPERIENCE</a>
            <a href="/contact" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>CONTACT</a>
          </nav>
        </div>
      </div>
    </header>
  );
};

// Yacht Card Component for destination page
const YachtCard = ({ yacht, onBookNow }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    // Use SEO-friendly URL if available, fallback to old format
    const url = yacht.seo_url || `/yacht/${yacht.id}`;
    navigate(url);
  };

  return (
    <div className="group cursor-pointer">
      <div className="relative mb-6 overflow-hidden" onClick={handleCardClick}>
        <img 
          src={yacht.image_url} 
          alt={yacht.name}
          className="w-full h-80 object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-sm px-3 py-1">
          <span className="text-xs font-light text-white tracking-wide">{yacht.type}</span>
        </div>
        <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onBookNow(yacht);
            }}
            className="px-6 py-2 border border-white/50 text-white text-xs font-light tracking-wide hover:bg-white hover:text-charcoal-900 transition-all duration-300 backdrop-blur-sm"
          >
            BOOK NOW
          </button>
        </div>
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="w-10 h-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <h3 className="text-xl font-light text-charcoal-900 tracking-wide">{yacht.name}</h3>
          <div className="text-right">
            <p className="text-lg font-light text-charcoal-900">
              €{yacht.price_per_day ? yacht.price_per_day.toLocaleString() : 'On Request'}
            </p>
            <p className="text-xs font-light text-charcoal-500 tracking-wide uppercase">per day</p>
          </div>
        </div>
        
        <p className="text-sm font-light text-charcoal-600 leading-relaxed line-clamp-2">
          {yacht.description}
        </p>
        
        <div className="flex justify-between items-center text-xs font-light text-charcoal-500 tracking-wide uppercase pt-2">
          <div className="flex items-center">
            <MapPin className="w-3 h-3 mr-1" />
            {yacht.location}
          </div>
          <div className="flex items-center">
            <Users className="w-3 h-3 mr-1" />
            {yacht.guests} guests
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Destination Page Component
const DestinationPage = ({ onBookNow }) => {
  const { destinationId } = useParams();
  const navigate = useNavigate();
  const [yachts, setYachts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItinerary, setSelectedItinerary] = useState(null);

  const destination = destinationData[destinationId];

  useEffect(() => {
    if (!destination) {
      navigate('/');
      return;
    }
    fetchDestinationYachts();
  }, [destinationId, destination, navigate]);

  const fetchDestinationYachts = async () => {
    try {
      setLoading(true);
      // Filter yachts by destination location using the filterLocation property
      const locationFilter = destination.filterLocation;
      console.log('🔍 Fetching yachts for location:', locationFilter);
      const response = await axios.get(`${API}/yachts?location=${locationFilter}`);
      console.log('📡 API Response:', response.data);
      
      // Handle different response formats
      if (response.data && response.data.yachts) {
        // New API format with pagination
        setYachts(response.data.yachts);
      } else if (Array.isArray(response.data)) {
        // Direct array format
        setYachts(response.data);
      } else {
        // Fallback
        console.warn('⚠️ Unexpected API response format:', response.data);
        setYachts([]);
      }
    } catch (error) {
      console.error('Error fetching destination yachts:', error);
      setYachts([]);
    } finally {
      setLoading(false);
    }
  };

  if (!destination) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-light text-charcoal-900 mb-4">Destination not found</h1>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-charcoal-900 text-white text-sm font-light tracking-wide hover:bg-charcoal-800 transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header isTransparent={true} />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${destination.heroImage}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <h1 className="text-sm font-light tracking-[0.3em] mb-4 opacity-90 uppercase">{destination.subtitle}</h1>
            <h2 className="text-6xl lg:text-8xl font-thin leading-none mb-6 tracking-tight">
              {destination.name.toUpperCase()}
            </h2>
            <p className="text-xl lg:text-2xl font-light leading-relaxed opacity-80 max-w-4xl mx-auto">
              {destination.description}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <div className="flex items-center text-sm font-light opacity-75">
              <Calendar className="w-4 h-4 mr-2" />
              {destination.season}
            </div>
            <div className="flex items-center text-sm font-light opacity-75">
              <Sun className="w-4 h-4 mr-2" />
              {destination.temperature}
            </div>
            <div className="flex items-center text-sm font-light opacity-75">
              <Waves className="w-4 h-4 mr-2" />
              {destination.conditions}
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-24 bg-warm-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Experience</h2>
            <h3 className="text-4xl font-thin text-charcoal-900 mb-6 tracking-tight">Exceptional Moments</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {destination.highlights.map((highlight, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-sm shadow-sm border border-warm-gray-100">
                <div className="w-12 h-12 bg-charcoal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  {index === 0 && <Waves className="w-5 h-5 text-charcoal-600" />}
                  {index === 1 && <Palmtree className="w-5 h-5 text-charcoal-600" />}
                  {index === 2 && <Sun className="w-5 h-5 text-charcoal-600" />}
                  {index === 3 && <Fish className="w-5 h-5 text-charcoal-600" />}
                  {index === 4 && <Compass className="w-5 h-5 text-charcoal-600" />}
                  {index === 5 && <Anchor className="w-5 h-5 text-charcoal-600" />}
                </div>
                <h4 className="text-lg font-light text-charcoal-900 mb-2">{highlight}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Itineraries Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Dream Itineraries</h2>
            <h3 className="text-4xl font-thin text-charcoal-900 mb-6 tracking-tight">Unforgettable Journeys</h3>
            <p className="text-lg font-light text-charcoal-600 leading-relaxed max-w-3xl mx-auto">
              Let yourself be guided towards extraordinary experiences, where each stop becomes an imperishable memory.
            </p>
          </div>

          <div className="space-y-16">
            {destination.itineraries.map((itinerary, index) => (
              <div key={itinerary.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="mb-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <span className="px-3 py-1 bg-charcoal-100 text-xs font-light text-charcoal-600 rounded-full uppercase tracking-wide">
                        {itinerary.duration}
                      </span>
                      <span className="px-3 py-1 bg-blue-100 text-xs font-light text-blue-600 rounded-full uppercase tracking-wide">
                        {itinerary.difficulty}
                      </span>
                    </div>
                    <h3 className="text-3xl font-thin text-charcoal-900 mb-4 tracking-tight">
                      {itinerary.title}
                    </h3>
                    <p className="text-lg font-light text-charcoal-700 leading-relaxed mb-6">
                      {itinerary.description}
                    </p>
                  </div>

                  {/* Dream Description */}
                  <div className="bg-gradient-to-br from-blue-50 to-teal-50 p-8 rounded-sm border-l-4 border-blue-200 mb-8">
                    <h4 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-3 uppercase">
                      Dream...
                    </h4>
                    <p className="text-base font-light text-charcoal-700 leading-relaxed italic">
                      {itinerary.dreamDescription}
                    </p>
                  </div>

                  {/* Highlights */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">
                      Highlights
                    </h4>
                    {itinerary.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-charcoal-400 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-sm font-light text-charcoal-600 leading-relaxed">{highlight}</p>
                      </div>
                    ))}
                  </div>

                  {/* Route */}
                  <div className="mt-8 p-6 bg-warm-gray-50 rounded-sm">
                    <h4 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-3 uppercase">
                      Route
                    </h4>
                    <p className="text-sm font-light text-charcoal-700 leading-relaxed">
                      {itinerary.route}
                    </p>
                  </div>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative overflow-hidden rounded-sm h-96 lg:h-[600px]">
                    <img 
                      src={destination.heroImage} 
                      alt={itinerary.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 text-white">
                      <h4 className="text-lg font-light tracking-wide mb-2">{itinerary.title}</h4>
                      <p className="text-sm font-light opacity-80">{itinerary.duration}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Yachts Section - Filtered for this destination */}
      <section className="py-24 bg-charcoal-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-sm font-light tracking-[0.3em] mb-4 uppercase opacity-80">Available Fleet</h2>
            <h3 className="text-4xl font-thin mb-6 tracking-tight">Yachts Departing from {destination.name}</h3>
            <p className="text-lg font-light opacity-70 leading-relaxed max-w-3xl mx-auto">
              Discover our exclusive selection of yachts based in {destination.name}, ready to take you to your wildest dreams.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
              <div className="text-sm font-light opacity-70">Loading fleet...</div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                {yachts.map(yacht => (
                  <YachtCard 
                    key={yacht.id} 
                    yacht={yacht} 
                    onBookNow={onBookNow}
                  />
                ))}
              </div>
              
              {yachts.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-lg font-light opacity-70">No yachts currently available departing from {destination.name}.</p>
                </div>
              )}
            </>
          )}

          {/* Call to Action */}
          <div className="text-center mt-20">
            <div className="space-y-6">
              <h4 className="text-2xl font-thin tracking-tight">Ready for Adventure?</h4>
              <p className="text-base font-light opacity-80 max-w-2xl mx-auto">
                Our experts create your personalized itinerary according to your desires and wildest dreams.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => navigate('/contact')}
                  className="px-8 py-4 bg-white text-charcoal-900 text-sm font-light tracking-wide hover:bg-warm-gray-100 transition-all uppercase"
                >
                  Plan My Journey
                </button>
                <button 
                  onClick={() => navigate('/')}
                  className="px-8 py-4 border border-white/30 text-white text-sm font-light tracking-wide hover:bg-white/10 transition-all uppercase"
                >
                  Discover Other Destinations
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal-900 text-white py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-light tracking-wide mb-4">
            PRIVIA<span className="font-thin">CHARTER</span>
          </h3>
          <p className="text-sm font-light text-white/70">
            The art of escape for privileged souls seeking more than luxury.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default DestinationPage;